import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaEdit, FaTrash, FaBan, FaCheck, FaPhone, FaEnvelope, FaMapMarkerAlt, FaCalendarAlt, FaCalendar, FaStar, FaChild, FaHistory, FaExclamationCircle, FaGift } from 'react-icons/fa';
import api from "../../utils/api.service";
import { ENDPOINTS } from "../../utils/api-config";
import { toast } from "react-toastify";
import "./Customers.css";
import ConfirmModal from '../Common/ConfirmModal';
import SuspendModal from '../Common/SuspendModal';
import EditPostModal from '../Common/EditPostModal';
import ViewPostModal from '../Common/ViewPostModal';
import { FaEye } from 'react-icons/fa';

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [payments, setPayments] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('bookings');

  const [suspendModal, setSuspendModal] = useState({
    isOpen: false
  });

  const [activateModal, setActivateModal] = useState({
    isOpen: false
  });

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false
  });

  const [postToDelete, setPostToDelete] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [viewingPost, setViewingPost] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  useEffect(() => {
    fetchCustomerDetails();
  }, [id]);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      try {
        const res = await api.get(`${ENDPOINTS.CUSTOMERS.base}/${id}`);
        const customerData = res.data;
        setCustomer(customerData);

        const extractedPayments = (customerData.bookings || [])
          .filter(b => b.payment)
          .map(b => ({
            ...b.payment,
            booking: b
          }))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setPayments(extractedPayments);
      } catch (err) {
        console.error("Failed to fetch customer main data:", err);
        throw err;
      }

      try {
        const postRes = await api.get(ENDPOINTS.COMMUNITY.USER_POSTS(id));
        setPosts(postRes.data?.posts || postRes.data || []);
      } catch (err) {
        console.error("Failed to fetch customer posts:", err);
      }

      try {
        const favRes = await api.get(`/favorites/${id}`);
        setFavorites(favRes.data || []);
      } catch (err) {
        console.error("Failed to fetch favorites:", err);
      }
    } catch (err) {
      setError(err.message || "Failed to load customer details");
    } finally {
      setLoading(false);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url;
    const baseUrl = ENDPOINTS.AUTH.LOGIN.replace('/api/auth/login', '').replace('/auth/login', '');
    return `${baseUrl}/${url.startsWith('/') ? url.slice(1) : url}`;
  };

  const formatAddress = (c) => {
    if (!c) return "No address";
    const parts = [
      c.unitNumber,
      c.address,
      c.city,
      c.zipCode
    ].filter(p => !!p);

    return parts.length > 0 ? parts.join(', ') : "No address";
  };

  const confirmSuspend = async (reason) => {
    try {
      setProcessing(true);
      await api.post(`${ENDPOINTS.CUSTOMERS.base}/${id}/block`, {
        reason: reason || "Admin blocked",
        isBlocked: true
      });
      toast.success("Customer suspended successfully");
      setSuspendModal({ isOpen: false });
      fetchCustomerDetails();
    } catch {
      toast.error("Failed to suspend member");
    } finally {
      setProcessing(false);
    }
  };

  const confirmActivate = async () => {
    try {
      setProcessing(true);
      await api.post(`${ENDPOINTS.CUSTOMERS.base}/${id}/unblock`);
      toast.success("Customer account reactivated");
      setActivateModal({ isOpen: false });
      fetchCustomerDetails();
    } catch {
      toast.error("Failed to reactivate member");
    } finally {
      setProcessing(false);
    }
  };

  const confirmDelete = async () => {
    try {
      setProcessing(true);
      await api.delete(`${ENDPOINTS.CUSTOMERS.base}/${id}`);
      toast.success("Customer profile permanently deleted");
      navigate('/customers');
    } catch {
      toast.error("Failed to delete member");
    } finally {
      setProcessing(false);
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setIsEditModalOpen(true);
  };

  const handleViewPost = (post) => {
    setViewingPost(post);
    setIsViewModalOpen(true);
  };

  const handleDeletePost = (postId) => {
    setPostToDelete(postId);
  };

  const confirmPostDelete = async () => {
    if (!postToDelete) return;
    try {
      setProcessing(true);
      await api.delete(ENDPOINTS.COMMUNITY.DELETE_POST(postToDelete));
      setPosts(posts.filter(p => p.id !== postToDelete));
      toast.success("Post deleted successfully");
    } catch (err) {
      console.error("Failed to delete post:", err);
      toast.error("Failed to delete post");
    } finally {
      setProcessing(false);
      setPostToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner-container">
        <div className="modern-spinner" />
        <p className="loading-text">Loading profile details...</p>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="empty-state">
        <h3>{error || "Customer not found"}</h3>
        <button onClick={() => navigate("/customers")} className="btn btn-primary">Back to List</button>
      </div>
    );
  }

  return (
    <div className="customer-details-page">

      <div className="page-header">
        <button onClick={() => navigate("/customers")} className="btn btn-secondary">
          <FaArrowLeft /> Back
        </button>

        <div className="header-actions">
          {customer.isBlocked ? (
            <button
              className="btn btn-outline-success"
              onClick={() => setActivateModal({ isOpen: true })}
              disabled={processing}
            >
              <FaCheck /> Reactivate Member
            </button>
          ) : (
            <button
              className="btn btn-outline-danger"
              onClick={() => setSuspendModal({ isOpen: true })}
              disabled={processing}
            >
              <FaBan /> Suspend Member
            </button>
          )}

          {/* <button 
              className="btn btn-outline-danger" 
              onClick={() => setDeleteModal({ isOpen: true })}
              disabled={processing}
            >
              <FaTrash /> Delete Profile
            </button> */}
        </div>
      </div>

      <div className="content-card customer-header-card modern">
        <div className="customer-header-content">
          <div className="avatar-wrapper">
            <img
              src={customer.avatar || `https://ui-avatars.com/api/?name=${customer.firstName}+${customer.lastName}&background=50C8AC&color=fff&size=200`}
              alt="avatar"
            />
          </div>

          <div className="customer-info">
            <h2>{customer.firstName} {customer.lastName}</h2>

            <div className="badges">
              {customer.isBlocked && <span className="badge danger">Blocked</span>}
              {customer.isFlagged && <span className="badge warning">Flagged</span>}
              <span className={`badge tier tier-${(customer.loyaltyTier || 'bronze').toLowerCase()}`}>
                {customer.loyaltyTier || 'Bronze'} Member
              </span>
            </div>

            <div className="contact-grid">
              <span><FaEnvelope /> {customer.email}</span>
              <span><FaPhone /> {customer.phone || "No phone linked"}</span>
              <span><FaMapMarkerAlt /> {formatAddress(customer)}</span>
              {customer.referralCode && (
                <span className="referral-badge">
                  <FaGift /> ID: {customer.referralCode}
                </span>
              )}
            </div>

            {customer.addresses && customer.addresses.length > 1 && (
              <div className="additional-addresses" style={{ marginTop: '15px', padding: '12px', borderTop: '1px solid #E2E8F0', fontSize: '0.85em' }}>
                <h5 style={{ margin: '0 0 8px 0', color: '#64748B', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FaMapMarkerAlt style={{ fontSize: '0.9em' }} /> Other Saved Addresses
                </h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {customer.addresses.slice(1).map((addr, idx) => (
                    <span key={idx} style={{ color: '#475569' }}>
                      <strong style={{ color: '#0F172A' }}>{addr.label || 'Other'}:</strong> {formatAddress(addr)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* STATS TILES - PROFESSIONAL GRID */}
      <div className="stats-modern">
        <div className="stat">
          <FaCalendar />
          <div>
            <h3>{customer.bookings?.filter(b => b.status !== 'cancelled').length || 0}</h3>
            <p>Total Bookings</p>
          </div>
        </div>

        <div className="stat">
          <FaStar />
          <div>
            <h3>{customer.loyaltyPoints || 0}</h3>
            <p>Loyalty Points</p>
          </div>
        </div>

        <div className="stat">
          <FaGift />
          <div>
            <h3>
              ${(customer.bookings || [])
                .filter(b => b.status !== 'cancelled')
                .reduce((sum, b) => sum + Number(b.totalAmount || 0), 0)
                .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h3>
            <p>Total Revenue</p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
        <div className="left-column">
          {/* BOOKING HISTORY - CLEAN TABLE */}
          <div className="content-card modern">
            <div className="card-header">
              <h3 className="card-title">Booking History</h3>
              <span className="badge tier">{customer.bookings?.length || 0} Records</span>
            </div>

            {!customer.bookings?.length ? (
              <div className="empty-state-small">No bookings found for this customer.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Booking #</th>
                      <th>Service Details</th>
                      <th>Date</th>
                      <th>Revenue</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customer.bookings.map((b) => (
                      <tr key={b.id} onClick={() => navigate(`/bookings/${b.id}`)}>
                        <td><span className="transaction-id">{b.bookingNumber}</span></td>
                        <td>{b.service?.name || "Service Item"}</td>
                        <td>{new Date(b.scheduledDate || b.createdAt).toLocaleDateString()}</td>
                        <td>${Number(b.totalAmount || 0).toFixed(2)}</td>
                        <td>
                          <span className={`status ${b.status.toLowerCase()}`}>{b.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* PAYMENT HISTORY */}
          <div className="content-card modern">
            <div className="card-header">
              <h3 className="card-title">Payment Transactions</h3>
              <span className="badge tier">{payments.length || 0} Payments</span>
            </div>

            {!payments.length ? (
              <div className="empty-state-small">No payment history available.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Transaction ID</th>
                      <th>Provider</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td><code className="transaction-id">{p.stripePaymentIntentId || p.transactionId}</code></td>
                        <td>{p.booking?.provider?.firstName || 'Caregiver'}</td>
                        <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                        <td>${Number(p.amount || 0).toFixed(2)}</td>
                        <td><span className={`status ${p.status.toLowerCase()}`}>{(p.status || '').toUpperCase() === 'COMPLETED' ? 'Completed' : p.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="right-column">
          {/* LOYALTY & STATS - PROFESSIONAL BREAKDOWN */}
          <div className="content-card modern loyalty-section">
            <div className="card-header">
              <h3 className="card-title"><FaStar /> Loyalty Portfolio</h3>
            </div>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">Member Since</span>
                <span className="stat-value">{new Date(customer.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Available Points</span>
                <span className="stat-value primary">{customer.loyaltyPoints || 0} Pts</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Lifetime Value</span>
                <span className="stat-value">${Number(customer.totalSpent || 0).toFixed(2)}</span>
              </div>
              {/* <div className="stat-item">
                <span className="stat-label">Tier Status</span>
                <span className={`badge tier-${(customer.loyaltyTier || 'bronze').toLowerCase()}`}>
                  {customer.loyaltyTier || 'Bronze'}
                </span>
              </div> */}
            </div>
          </div>

          {/* FAMILY SECTION - CHILDREN */}
          <div className="content-card modern">
            <div className="card-header">
              <h3 className="card-title"><FaChild /> Registered Children</h3>
            </div>
            {!customer.children?.length ? (
              <div className="empty-state-small">No children profiles registered.</div>
            ) : (
              <div className="children-grid">
                {customer.children.map(child => (
                  <div key={child.id} className="child-card">
                    <div className="child-avatar">
                      {child.gender === 'female' ? '👧' : '👦'}
                    </div>
                    <div className="child-info">
                      <h4>{child.name}</h4>
                      <p>{child.age} years • {child.gender}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* COMPLETED SERVICES LIST */}
          <div className="content-card modern">
            <div className="card-header">
              <h3 className="card-title"><FaCheck /> Completed Services</h3>
            </div>
            {!customer.bookings?.filter(b => b.status?.toUpperCase() === 'COMPLETED').length ? (
              <div className="empty-state-small">No completed services yet.</div>
            ) : (
              <div className="services-list-compact">
                {customer.bookings
                  .filter(b => b.status?.toUpperCase() === 'COMPLETED')
                  .sort((a, b) => new Date(b.resolvedAt || b.updatedAt) - new Date(a.resolvedAt || a.updatedAt))
                  .map((b, idx) => (
                    <div key={idx} className="service-item-row">
                      <div className="service-item-main">
                        <div className="service-name-text">{b.service?.name || 'Care Service'}</div>
                        <div className="service-category-tag">
                          {new Date(b.resolvedAt || b.updatedAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="service-item-details text-end">
                        <div className="service-price-text">${Number(b.totalAmount || 0).toFixed(2)}</div>
                        <div className="service-type-label">with {b.provider?.firstName || 'Caregiver'}</div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* FAVORITE PORTFOLIO */}
          <div className="content-card modern">
            <div className="card-header">
              <h3 className="card-title text-warning"><FaStar /> Favorite Portfolio</h3>
              <span className="badge tier">{favorites.length} Items</span>
            </div>
            {!favorites.length ? (
              <div className="empty-state-small">No favorites bookmarked yet.</div>
            ) : (
              <div className="favorite-list-compact mt-3">
                {favorites.map((fav, idx) => (
                  <div key={idx} className="fav-item-row p-3 mb-2 rounded border bg-light" onClick={() => navigate(`/providers/${fav.id}`)} style={{ cursor: 'pointer' }}>
                    <div className="d-flex align-items-center gap-3">
                      <img
                        src={fav.avatar || `https://ui-avatars.com/api/?name=${fav.firstName}+${fav.lastName}&background=FFD700&color=000`}
                        alt="Provider"
                        className="rounded-circle shadow-sm"
                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                      />
                      <div className="flex-grow-1">
                        <div className="fw-bold text-dark">{fav.firstName} {fav.lastName}</div>
                        <div className="text-muted small">
                          {fav.services && fav.services.length > 0
                            ? (Array.isArray(fav.services) ? fav.services.join(', ') : fav.services)
                            : (fav.category?.name || 'General Care')}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* INTERNAL NOTES */}
          <div className="content-card modern notes-section" style={customer.isBlocked ? { borderLeft: '5px solid #EF4444' } : customer.isFlagged ? { borderLeft: '5px solid #F59E0B' } : {}}>
            <div className="card-header">
              <h3 className="card-title">Administrative Notes</h3>
            </div>
            <div className="notes-container">
              <div className="notes-box">
                <p className="notes-content">
                  {customer.internalNotes || "No internal administrative notes recorded for this member."}
                </p>
              </div>
              {(customer.isBlocked || customer.isFlagged) && (
                <div className="reason-box">
                  {customer.isBlocked && <p className="reason text-danger">🛑 <strong>Security Block:</strong> {customer.blockedReason || 'No reason specified'}</p>}
                  {customer.isFlagged && <p className="reason text-warning">🚩 <strong>Quality Flag:</strong> {customer.flagReason || 'No reason specified'}</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* COMMUNITY ACTIVITY - REFINED FEED */}
      <div className="content-card modern community-section">
        <div className="card-header">
          <h3 className="card-title">Community Contribution</h3>
          <span className="badge tier">{posts.length || 0} Contributions</span>
        </div>
        {!posts.length ? (
          <div className="empty-state-small">This member hasn't shared any community posts yet.</div>
        ) : (
          <div className="posts-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px', padding: '16px' }}>
            {posts.map((post) => (
              <div key={post.id} className="post-item-card" style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10, display: 'flex', gap: '8px' }}>
                  <button
                    className="btn-view-post"
                    onClick={() => handleViewPost(post)}
                    title="View Post"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(16, 185, 129, 0.9)', // Emerald color for View
                      color: 'white',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      transition: 'transform 0.2s'
                    }}
                  >
                    <FaEye size={14} />
                  </button>
                  {/* <button 
                    className="btn-edit-post"
                    onClick={() => handleEditPost(post)}
                    title="Edit Post"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(59, 130, 246, 0.9)',
                      color: 'white',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      transition: 'all 0.2s'
                    }}
                  >
                    <FaEdit size={14} />
                  </button> */}
                  <button
                    className="btn-delete-post"
                    onClick={() => handleDeletePost(post.id)}
                    title="Delete Post"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(239, 68, 68, 0.9)',
                      color: 'white',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      transition: 'all 0.2s'
                    }}
                  >
                    <FaTrash size={14} />
                  </button>
                </div>
                <div className="post-media-container" style={{ position: 'relative', height: '200px', backgroundColor: '#f1f5f9' }}>
                  {post.mediaUrls && post.mediaUrls.length > 0 ? (
                    <img
                      src={getFullUrl(post.mediaUrls[0])}
                      alt="Post"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Image+Unavailable'; }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>No Media Content</div>
                  )}
                </div>
                <div className="post-body">
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '1.1rem', color: '#0f172a' }}>{post.title || "Observation Note"}</h4>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#475569', lineHeight: '1.6' }}>{post.content}</p>
                  <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: '0.8rem' }}>
                    <span>❤️ {post.likesCount || 0} Likes</span>
                    <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODALS */}
      <SuspendModal
        isOpen={suspendModal.isOpen}
        onClose={() => setSuspendModal({ isOpen: false })}
        onConfirm={confirmSuspend}
        memberName={customer?.firstName + ' ' + customer?.lastName}
        title="Account Suspension"
        message="Please provide a reason for suspending this member's access to the platform."
      />

      <ConfirmModal
        isOpen={activateModal.isOpen}
        onClose={() => setActivateModal({ isOpen: false })}
        onConfirm={confirmActivate}
        title="Reactivate Member Account?"
        message={`This will restore access for ${customer?.firstName}. They will be able to log in and use all platform features again.`}
        confirmText="Reactivate Account"
        type="warning"
      />

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false })}
        onConfirm={confirmDelete}
        title="Permanently Delete Profile?"
        message="Warning: This will remove all associated data including bookings, children profiles, and history. This action cannot be reversed."
        confirmText="Permanently Delete"
        type="danger"
      />

      <ConfirmModal
        isOpen={!!postToDelete}
        onClose={() => setPostToDelete(null)}
        onConfirm={confirmPostDelete}
        title="Delete Community Post?"
        message="Are you sure you want to delete this community post? This action cannot be undone."
        confirmText="Delete Post"
        type="danger"
      />

      <EditPostModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        post={editingPost}
        onSuccess={fetchCustomerDetails}
      />

      <ViewPostModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        post={viewingPost}
      />
    </div>
  );
};

export default CustomerDetails;
